(() => {
  const root = document.querySelector('[data-cosmora-email-popup]');
  if (!root) return;

  const overlay = root.querySelector('[data-cosmora-popup-overlay]');
  const dialog = root.querySelector('.cosmora-email-popup__dialog');
  const backdrop = root.querySelector('[data-cosmora-popup-backdrop]');
  const closeButtons = root.querySelectorAll('[data-cosmora-popup-close]');
  const tab = root.querySelector('[data-cosmora-popup-tab]');
  const tabText = root.querySelector('.cosmora-email-popup__tab-text');
  const tabArrow = root.querySelector('.cosmora-email-popup__tab-arrow');
  const tabDismiss = root.querySelector('[data-cosmora-popup-tab-dismiss]');
  const emailInput = root.querySelector('input[type="email"]');
  const success = root.querySelector('[data-cosmora-popup-success]');
  const error = root.querySelector('[data-cosmora-popup-error]');

  if (!overlay || !dialog) return;

  const joinedKey = 'cosmoraEmailPopupJoined';
  const confirmedKey = 'cosmoraEmailConfirmed';
  const dismissedKey = 'cosmoraEmailPopupDismissedAt';
  const tabDismissedKey = 'cosmoraEmailPopupTabDismissedAt';

  const discountCode = root.dataset.discountCode || 'COSMORA10';
  const liquidConfirmed = root.dataset.confirmed === 'true';
  const delay = Number(root.dataset.delay || 7000);
  const dismissDays = Number(root.dataset.dismissDays || 7);
  const dismissMs = dismissDays * 24 * 60 * 60 * 1000;

  let previouslyFocused = null;
  let openTimer = null;
  let copiedTimer = null;

  const storageGet = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (_) {
      return null;
    }
  };

  const storageSet = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (_) {}
  };

  const storageRemove = (key) => {
    try {
      window.localStorage.removeItem(key);
    } catch (_) {}
  };

  const isRecent = (key) => {
    const value = Number(storageGet(key));
    return Boolean(value && Date.now() - value < dismissMs);
  };

  const isConfirmed = () =>
    liquidConfirmed || storageGet(confirmedKey) === '1';

  const isOpen = () => !overlay.hidden;

  const setOfferTab = () => {
    if (!tab) return;
    tab.classList.remove('is-code');
    tab.setAttribute('aria-label', 'Open 10% off email signup');
    if (tabText) tabText.textContent = 'GET 10% OFF';
    if (tabArrow) tabArrow.textContent = '→';
  };

  const setCodeTab = (copied = false) => {
    if (!tab) return;
    tab.classList.add('is-code');
    tab.setAttribute(
      'aria-label',
      copied ? `Copied discount code ${discountCode}` : `Copy discount code ${discountCode}`
    );
    if (tabText) tabText.textContent = copied ? 'COPIED ✓' : discountCode;
    if (tabArrow) tabArrow.textContent = copied ? '' : 'COPY';
  };

  const showTab = () => {
    if (isRecent(tabDismissedKey)) return;

    if (isConfirmed()) {
      setCodeTab(false);
    } else {
      if (success || storageGet(joinedKey) === '1') return;
      setOfferTab();
    }

    if (tab) tab.hidden = false;
    if (tabDismiss) tabDismiss.hidden = false;
  };

  const hideTab = () => {
    if (tab) tab.hidden = true;
    if (tabDismiss) tabDismiss.hidden = true;
  };

  const copyCode = async () => {
    let copied = false;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(discountCode);
        copied = true;
      }
    } catch (_) {}

    if (!copied) {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = discountCode;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        copied = document.execCommand('copy');
        textarea.remove();
      } catch (_) {}
    }

    if (copied) {
      setCodeTab(true);
      window.clearTimeout(copiedTimer);
      copiedTimer = window.setTimeout(() => setCodeTab(false), 1600);
    }
  };

  const openPopup = ({ fromTab = false } = {}) => {
    if (isConfirmed()) {
      showTab();
      return;
    }

    if (isOpen()) return;

    if (fromTab) {
      storageRemove(dismissedKey);
    }

    hideTab();
    previouslyFocused = document.activeElement;
    overlay.hidden = false;
    document.body.classList.add('cosmora-popup-open');

    requestAnimationFrame(() => {
      overlay.classList.add('is-open');
      window.setTimeout(() => {
        (emailInput || dialog)?.focus({ preventScroll: true });
      }, 190);
    });
  };

  const closePopup = ({ remember = true } = {}) => {
    if (!isOpen()) return;

    if (remember && !success) {
      storageSet(dismissedKey, String(Date.now()));
    }

    overlay.classList.remove('is-open');
    document.body.classList.remove('cosmora-popup-open');

    window.setTimeout(() => {
      overlay.hidden = true;

      if (isConfirmed() || (!success && storageGet(joinedKey) !== '1')) {
        showTab();
      }

      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus({ preventScroll: true });
      }
    }, 190);
  };

  closeButtons.forEach((button) => {
    button.addEventListener('click', () => closePopup({ remember: true }));
  });

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay || event.target === backdrop) {
      closePopup({ remember: true });
    }
  });

  tab?.addEventListener('click', () => {
    if (isConfirmed()) {
      copyCode();
    } else {
      openPopup({ fromTab: true });
    }
  });

  tabDismiss?.addEventListener('click', (event) => {
    event.stopPropagation();
    storageSet(tabDismissedKey, String(Date.now()));
    hideTab();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOpen()) {
      closePopup({ remember: true });
      return;
    }

    if (event.key !== 'Tab' || !isOpen()) return;

    const focusable = [...dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((element) => !element.hasAttribute('hidden'));

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Logged-in confirmed popup subscribers get the code tab immediately.
  if (isConfirmed()) {
    storageSet(confirmedKey, '1');
    storageSet(joinedKey, '1');
    storageRemove(tabDismissedKey);
    hideTab();
    showTab();
    return;
  }

  // After submitting the signup form, wait for double opt-in confirmation.
  if (success) {
    storageSet(joinedKey, '1');
    storageSet(dismissedKey, String(Date.now()));
    hideTab();
    openPopup();
    return;
  }

  if (error) {
    openPopup();
    return;
  }

  if (storageGet(joinedKey) === '1') {
    hideTab();
    return;
  }

  if (isRecent(dismissedKey)) {
    showTab();
    return;
  }

  openTimer = window.setTimeout(() => openPopup(), Math.max(0, delay));

  window.addEventListener('pagehide', () => {
    if (openTimer) window.clearTimeout(openTimer);
    if (copiedTimer) window.clearTimeout(copiedTimer);
  }, { once: true });
})();
